import { ListDealsQueryParams } from '../types/list-deals-query-params';

/**
 * Query key factory for deal queries.
 * Provides consistent, hierarchical keys for cache management.
 */
export const dealQueryKeys = {
  all: ['deals'] as const,
  lists: (queryParams: Partial<ListDealsQueryParams> = {}, orgId?: string) =>
    [
      ...dealQueryKeys.all,
      'list',
      ...(orgId ? [orgId] : []),
      ...Object.entries(queryParams).map(([key, value]) => `${key}=${value}`),
    ] as const,
  list: (queryParams: Partial<ListDealsQueryParams> = {}, orgId?: string) =>
    [...dealQueryKeys.lists(queryParams, orgId)] as const,
  infiniteList: (
    queryParams: Partial<ListDealsQueryParams> = {},
    orgId?: string
  ) => [...dealQueryKeys.lists(queryParams, orgId), 'infinite'] as const,
  details: () => [...dealQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...dealQueryKeys.details(), id] as const,
  participantsBase: (dealId: string) =>
    [...dealQueryKeys.detail(dealId), 'participants'] as const,
  participants: (dealId: string, filters?: Record<string, unknown>) =>
    [...dealQueryKeys.participantsBase(dealId), filters] as const,
  /** Org-wide participant picker (infinite) for the /deals participant filter. */
  orgParticipants: (
    params: { search: string; archived: boolean },
    orgId?: string
  ) =>
    [
      ...dealQueryKeys.all,
      'org-participants',
      ...(orgId ? [orgId] : []),
      params.archived ? 'archived' : 'active',
      params.search,
    ] as const,
  /**
   * Summary of deals the *caller* created (count + most-recent created), backed
   * by `/api/deals/created-by-me`. Distinct from the participant-scoped
   * `myDealsQueryKeys`. Covered by `dealQueryKeys.all` invalidations, so the
   * create/archive/clone mutations refresh it automatically.
   */
  createdSummary: () => [...dealQueryKeys.all, 'created-summary'] as const,
  /**
   * Synthetic Shared with Me folder backed by `/api/deals/shared-with-me`.
   * Scoped by org id so switching organizations never reuses stale rows.
   */
  sharedWithMe: (orgId?: string) =>
    [
      ...dealQueryKeys.all,
      'shared-with-me',
      ...(orgId ? [orgId] : []),
    ] as const,
  /**
   * One level of the deal folder tree (folders + deal leaves) from
   * `/api/deals/tree`. `parentId` omitted means the org deal root's children.
   */
  tree: (params: { parentId?: string; offset?: number } = {}, orgId?: string) =>
    [
      ...dealQueryKeys.all,
      'tree',
      ...(orgId ? [orgId] : []),
      params.parentId ?? 'root',
      ...(params.offset !== undefined ? [`offset=${params.offset}`] : []),
    ] as const,
} as const;
